// @ts-strict-ignore
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { useUniqueId } from '@usealloy/component-library';
import { AggregationEditorContext } from './context';
import { StyledAggregationTextField } from './styles';
import { parseAndClamp } from './helpers';

export interface AggregationNumberInputProps {
  minValue: number;
  maxValue: number;
  value: number;
  onHandleValueChange: (value: number) => void;
  /** Number of decimal places allowed. 0 = integer only (default). */
  decimalScale?: number;
  className?: string;
}

const AggregationNumberInput = ({
  minValue,
  maxValue,
  value,
  onHandleValueChange,
  decimalScale = 0,
  className,
}: AggregationNumberInputProps) => {
  const { keyboardFocus } = useContext(AggregationEditorContext);

  const ref = useRef<HTMLInputElement>(null);
  const id = useUniqueId('aggregation-number-input');

  const displayValue = Number.isFinite(value) ? value : minValue;

  useEffect(() => {
    keyboardFocus?.register({ id, ref });
    return () => keyboardFocus?.unregister(id);
  }, [id, keyboardFocus]);

  const commitValue = useCallback(
    (rawValue: string) => {
      const committed = parseAndClamp({
        rawValue,
        clampValues: { min: minValue, max: maxValue },
        decimalScale,
      });
      onHandleValueChange(committed);
    },
    [minValue, maxValue, decimalScale, onHandleValueChange],
  );

  return (
    <span className={className} data-aggregation-number-input>
      <StyledAggregationTextField
        ref={ref}
        id={id}
        type="number"
        widthFitContent
        value={String(displayValue)}
        onChange={event => commitValue(event.target.value)}
        onBlur={() => commitValue(String(displayValue))}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commitValue(ref.current?.value ?? String(displayValue));
            ref.current?.blur();
          }
        }}
      />
    </span>
  );
};

export default AggregationNumberInput;
